import { StyleSheet, Dimensions, Platform, StatusBar } from "react-native";
import { isTablet } from "react-native-device-info";

const bgheight = Dimensions.get('window').height;
const bgwidth = Dimensions.get('window').width;

// Unified responsive utilities for both web and native
const getResponsiveValue = (baseValue: number, scaleFactor = 0.8, minScale = 0.5) => {
  const screenWidth = Dimensions.get('window').width;
    const screenHeight = Dimensions.get('window').height;
    const isSmallScreen = screenWidth < 400;
    const isTabletDevice = isTablet();

  let factor = scaleFactor;
  
    if (Platform.OS === 'web') {
    // Web responsive scaling
        if (screenWidth < 768) factor = 0.9;  // Mobile web
        else if (screenWidth < 1024) factor = 0.85; // Tablet web  
        else if (screenWidth < 1440) factor = 0.8;  // Laptop web
        else factor = 0.85; // Large desktop
    } else {
        // Native responsive scaling
        if (isSmallScreen) {
            factor = 0.85; // Small phones - slightly smaller
        } else if (isTabletDevice) {
            factor = 0.95; // Tablets - almost full size
        } else {
            factor = 0.9; // Regular phones - slightly reduced
        }
    }
  
    return Math.max(baseValue * factor, baseValue * minScale);
};

// Unified responsive functions (work on both web and native)
const responsivePadding = (basePadding: number) => getResponsiveValue(basePadding, 0.7, 0.5);
const responsiveFontSize = (baseFontSize: number) => Math.max(getResponsiveValue(baseFontSize, 0.9, 0.6), 12);
const responsiveHeight = (baseHeight: number) => Math.max(getResponsiveValue(baseHeight, 0.8, 0.5), 36);

// Legacy web-only functions (kept for backward compatibility)
const getWebResponsiveValue = (baseValue: number, scaleFactor = 0.8) => {
    if (Platform.OS !== 'web') return baseValue;
    return getResponsiveValue(baseValue, scaleFactor);
};

const webResponsivePadding = (basePadding: number) => responsivePadding(basePadding);
const webResponsiveFontSize = (baseFontSize: number) => responsiveFontSize(baseFontSize);
const webResponsiveHeight = (baseHeight: number) => responsiveHeight(baseHeight);

// Modern Design System Utilities
const getModernCardStyle = (theme: boolean, size: 'small' | 'medium' | 'large' = 'medium') => {
  const baseCard = size === 'small' ? styles.modernCardSmall : 
                   size === 'large' ? styles.modernCardLarge : 
                   styles.modernCard;
  
  return {
    ...baseCard,
    ...(theme ? styles.modernBgLight : styles.modernBgDark)
  };
};

const getModernActionStyle = (theme: boolean, color: 'green' | 'blue' | 'red' | 'amber' | 'grey' = 'blue') => {
  const colorMap = {
    green: theme ? styles.modernActionGreen : styles.modernActionGreenDark,
    blue: theme ? styles.modernActionBlue : styles.modernActionBlueDark,
    red: theme ? styles.modernActionRed : styles.modernActionRedDark,
    amber: theme ? styles.modernActionAmber : styles.modernActionAmberDark,
      grey: theme ? styles.modernActiongrey : styles.modernActiongreyDark,
  };
  
    return { ...styles.modernActionButton, flex: 1, minWidth: 120, ...colorMap[color]
};
};

const getModernTextColor = (theme: boolean, variant: 'primary' | 'secondary' | 'muted' = 'primary') => {
    return theme ? "black" : "white";
};

const styles = StyleSheet.create({
    slider:{ 
        width: "100%",
        height: 50,
    },
    snapPoint: {
        position: 'absolute',
        backgroundColor: 'blue', // Customize color
        width: 10, // Size of the snap point
        height: 10,
        borderRadius: 5, // Makes the point circular
    },
    contextComponentWrapper: {
        flex: 1,
        alignSelf: "stretch",
        minWidth: "100%",
        maxHeight: "90%"
    },
    contextStyle: {
        flex: 15,
        marginVertical: "5%",
        alignSelf: "stretch",
        minWidth: "100%"
    },
    noticeCardContainer:{
        flexDirection: "row", gap: 30, borderRadius: 20, backgroundColor: "#ececec", margin: 10, padding: 10, shadowRadius: 20, shadowOpacity: .2
    },
    infoTextStyle: {
        fontSize: 84,
        textAlign: "center",
        marginVertical: 20,
    },
    //Texts
    headingText: {
        fontSize: webResponsiveFontSize(32),
        fontWeight: '600'
    },
    validationView: {
        flexWrap: 'wrap',
        flex: 1,
        padding: webResponsivePadding(10),
        gap: webResponsivePadding(15),
    },
    defaultShadow: {
        shadowOpacity: .2,
        shadowRadius: 20,
    },
    defaultRoundedShadow: {
        borderRadius: 20,
        padding:10,
        paddingHorizontal: 20
    },
    timelineItem: {
        width: 80, // Adjust as needed
        height: 80, // Adjust as needed
        borderRadius: 40,
        transform: [{ scale: 0.8 }],
        borderWidth: 2,
        borderColor: "white",
        backgroundColor: 'rgb(90,192,234)', // Adjust as needed
        justifyContent: 'center',
        alignItems: 'center',
        marginHorizontal: 8,
        padding: 7 // Adjust as needed
    },
    contextContainer: {
        padding: webResponsivePadding(20),
        alignSelf: 'stretch',
        flex: 1,
        paddingBottom: webResponsivePadding(50),
    },
    contextWrapper: {
        flex:1,
    },
    dataBrowserItemStyle:{
        backgroundColor: 'transparent',
        minHeight: 400,
        flex: 1,
        alignSelf: 'stretch',
    },
    dataContainerStyle: {
        padding: webResponsivePadding(10),
        minWidth: Platform.OS === 'web' ? Math.max(getWebResponsiveValue(350), 280) : 350,
        // width: (Platform.OS === 'web' ? "100%" : "105%"),
        alignSelf: 'stretch',
        flex: 10,
        borderRadius: webResponsivePadding(20),
        borderTopEndRadius: 0,
        borderBottomEndRadius: 0,
        overflow: 'visible',
        
        shadowRadius: webResponsivePadding(20),
        shadowOpacity: 0.2,
        shadowColor: 'grey'
    },
    extraSearchBarComponents: {
        alignSelf: 'stretch', flex: 1, flexDirection: 'row', alignContent: 'space-around', borderRadius: 15, backgroundColor: 'white', 
    },
    settingsRow: {
        flex: 1,
        flexDirection: Platform.OS === 'web' ? 'row' : 'column',
        maxWidth: '100%', padding: Platform.OS === 'web' ? 15 : undefined,
        maxHeight: '100%',
        flexWrap: 'wrap',
        justifyContent: 'center',
        marginVertical: 10
    },
    iconStyleUpload: {
        borderRadius: 20,
        shadowRadius: 10,
        minWidth: 200,
        shadowOpacity: 0.2,
        backgroundColor: "white",
        flex: 1,
        gap: 10,
        maxHeight: 150
    },
    contextViewStyle: {
        flex: 1,
        backgroundColor: 'white',
        minWidth: 320,
        borderRadius: 20,
        padding: 20,
        marginVertical: 5
    },
    extendedMenu: {
        flex: 1,
        backgroundColor: '#ececec',
        borderRadius: 30,
        minHeight: 300
    },
    welcomeScreenSideBar: { minWidth: '100%', padding: 10 },
    text: {
        color: "white",
        fontSize: 24,
        fontWeight: "bold",
        textAlign: "center",
        flex: 1,
    },
    channelStyle: { flex: 1, paddingHorizontal: 5, shadowRadius: 20, shadowOpacity: .2, borderRadius: 10, height: 50, paddingVertical: 2 },
    uploadText: {
        color: "black",
        fontWeight: "bold",
        textAlign: "center",
        flex: 1,
    },
    rowItem: {
        alignItems: "center",
        justifyContent: "center",
        // flex: 1,
        padding: 5,
        borderRadius: 15
    },
    centerEverything: {
        alignContent: 'center',
        alignItems: 'center',
        alignSelf: 'center',
    },
    searchSwipeIcons: {
        alignContent: 'center',
        alignItems: 'center',
        alignSelf: 'center',
        textAlign: 'center',
        flex: 1,
        backgroundColor: 'white',
        borderRadius: 15,
        maxWidth: 110,
        height: 70,
        padding: 15
    },
    paragraphText: {
        fontSize: 16,
    },
    flipCard: {
        flex: 1,
    },

    face: {
        flex: 1,
        backfaceVisibility: 'hidden',
        backgroundColor: 'transparent'
    },
    back: {
        flex: 7,
    },
    SplitBoxesFocused: {
        borderColor: 'rgba(90, 192, 239, 1)',
        borderWidth: 3,
        borderRadius: 5,
        padding: 12,
        minWidth: 50,
        backgroundColor: 'white',
        marginHorizontal: 3,
        minHeight: 60,
    },
    SplitBoxes: {
        fontSize: 20,
        textAlign: 'center',
        color: 'black',
        borderWidth: 3,
        borderColor: 'grey',
        minWidth: 50,
        minHeight: 60,
        borderRadius: 5,
        backgroundColor: 'white',
        marginHorizontal: 3,
        padding: 12
    },
    AndroidSafeArea: {
        flex: 1,
        marginTop: Platform.select({
            ios: -70,
            android: -25,
            web: -5
        }),
        marginBottom: Platform.select({
            ios: -40,
            android: -30,
            web: 0
        }),
        paddingTop: Platform.select({
            android: 50,
            web: 0
        })
    },
    credentialInputContainer: {
        alignItems: 'center',
        textAlign: 'left'
    },
    messageSearchContainer: {
        alignItems: 'center',
        textAlign: 'left',
        flex: 1,
    },
    statusBar: {
        flex: .07,
        
        zIndex: 10,
        shadowOffset: {
            width: 0,
            height: 0
        }
    },
    headerBar: {
        flex: 1.5,
        zIndex: 20,
        
        maxHeight: '6.5%'
    },
    footerBar: {
        minHeight: 30,
        maxHeight: 30,
    },
    credentialInputContainer2: {
        width: 450,
        alignItems: 'center',
    },
    inputContainer: {
        width: 450,
        margin: 10,
        alignContent: 'center',
    },
    registerInputContainer: {
        flex: 0.1,
        margin: 1,
        height: 10,
        padding: 2,
    },
    registerInputContainer3: {
        flex: 3,
        margin: 10,
        height: 15,
        padding: 2,
    },
    registerInputContainer2: {
        flex: 0.1,
        margin: 10,
        padding: 2,
        marginBottom: 300,
        height: 15,
    },
    inputStyle: {
        borderColor: "black",
        borderWidth: 1,
        padding: 10,
    },
    manageTeamWrapper: {
        width: 350,
    },
    manageTeamTitle: {
        marginBottom: 10,
    },
    addTeamMemberInput: {
        borderBottomColor: "black",
        borderBottomWidth: 1,
        marginTop: 5,
        fontSize: 18,
    },
    manageTeamButtonContainer: {
        textAlign: "left",
        borderTopColor: "grey",
        borderTopWidth: 1,
        borderBottomColor: "grey",
        borderBottomWidth: 1,
    },
    plusButton: {
        fontSize: 28,
        fontWeight: "400",
    },
    container: {
        alignItems: 'center',
        alignContent: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        alignSelf: 'center',
        zIndex: -1,
        backgroundColor: '#eeeeee',
        flex: 1
    },
    lightContainer: {
        backgroundColor: '#d0d0c0',
    },
    darkContainer: {
        backgroundColor: '#242c40',
    },
    lightThemeText: {
        color: '#242c40',
    },
    darkThemeText: {
        color: '#d0d0c0',
    },
    animationContainer: {
        alignItems: 'center',
        position: 'absolute',
        width: '100%',
    },
    inputLabel: {
        color: 'black',
        textAlign: 'left',
        alignItems: 'flex-start'
    },
    input: {
        margin: 15,
        minHeight: 30,
        borderColor: '#4791bd',
        maxHeight: 35,
        width: Platform.OS === 'web' ? 245 : 325,
        minWidth: 200,
        borderRadius: 5,
        backgroundColor: 'white',
        flex: 1,
        alignSelf: 'center',
        paddingHorizontal: 10
    },
    inputOutline: {
        margin: 5,
        minHeight: 40,
        minWidth: Platform.OS === 'web' ? '90%' : undefined,
        maxWidth: 400,
        padding: 10,
        borderRadius: 5,
        left: Platform.select({web: -40, native: undefined}),
        flex: 1,
        alignSelf: 'stretch',
        borderWidth: 1,
        maxHeight: 55
    },
    validationTextInput: {
        minHeight: 40,
        borderColor: '#4791bd',
        maxWidth: 400,
        padding: 10,
        borderRadius: 5,
        flex: 1,
        alignSelf: 'stretch',
        borderWidth: 1,
        maxHeight: 40,
        minWidth: '90%',
        marginHorizontal: 0,
        marginVertical: 5
    },
    validationOptionContainer: {
        flexDirection: 'row', alignItems: 'center', flex: 1, alignSelf: 'stretch', minWidth: '100%', flexWrap: 'wrap'
    },
    wpDrag: {
        maxWidth: 20,
        height: "100%",
        flex: 1,
        alignSelf: "center",
        justifyContent: "center",
    },
    textRotate: {
        transform: [{ rotate: '-90deg' }],
        alignSelf: 'stretch',
        flex: 1,
        left: 35,
        minHeight: 100,
        minWidth: 100,
        textAlign: 'center',
        verticalAlign: 'middle'
    },
    iconStyle: {
        height: 30,
        width: 30,
        padding: 5,
        borderRadius: 10,
        backgroundColor: "white",
        justifyContent: "center",
        alignContent: "center",
        alignItems: "center",
        textAlign: "center",
        maxWidth: 35,
    },
    mailSideIconStyle: {
        shadowRadius: 20,
        shadowOpacity: 0.2,
        backgroundColor: "white",
        borderTopEndRadius: 20,
        borderBottomEndRadius: 20,
        alignSelf: "flex-end",
        padding: 20,
        paddingHorizontal: 10,
        width: 65,
        maxWidth: 65
    },
    searchInputMessages: {
        marginHorizontal: 15,
        marginVertical: 10,
        height: 30,
        borderColor: '#4791bd',
        minWidth: 245,
        padding: 10,
        borderRadius: 5,
        backgroundColor: 'white',
        flexWrap: 'wrap'
    },
    bottomSheet: {
        borderRadius: 20,
        backgroundColor: '#ececec',
        shadowRadius: 20,
        shadowOpacity: .2,
        gap: 5,
        borderTopEndRadius: 0,
        zIndex: 100,
        paddingHorizontal: "4%",
        padding: 10,
        
        width: '100%'
    },
    messageField: {
        marginVertical: 10,
        height: 30,
        borderColor: '#4791bd',
        minWidth: 260,
        padding: 10,
        backgroundColor: 'white',
    },
    userMessageListItem: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        flex: 1,
        marginHorizontal: -10,
        padding: 15,
        overflow: 'visible'
    },
    dropdownInput: {
        marginVertical: 15,
        paddingHorizontal: 4,
        borderColor: '#4791bd',
        borderRadius: 5,
        backgroundColor: 'white',
        flexWrap: 'wrap',
        flex: 1
    },
    input3: {
        margin: 10,
        height: 40,
        borderColor: '#4791bd',
        borderWidth: 1,
        minWidth: bgwidth < 400 ? bgwidth - 30 : 600,
        padding: 5,
        borderRadius: 15,
        backgroundColor: '#ffffff'
    },
    input2: {
        margin: 15,
        height: 30,
        backgroundColor: '#ECECEC',
        borderColor: '#4791bd',
        borderWidth: 1,
        minWidth: 225,
        padding: 5,
        borderRadius: 5
    },
    modalInput: {
        height: 30,
        borderColor: '#4791bd',
        borderWidth: 1,
        flex: 3,
        paddingHorizontal: 10,
        borderRadius: 10,
        backgroundColor: 'white'
    },
    noteInput: {
        margin: 5,
        borderWidth: 1,
        borderColor: '#4791bd',
        padding: 5,
        flex: 1,
        alignContent: 'stretch',
        width: '100%'
    },
    noteInputContainer: {
        margin: 15,
        padding: 5,
        flex: 1,
        alignContent: 'stretch',
        height: 100,
    },
    submitButton: {
        backgroundColor: '#4791bd',
        padding: 10,
        margin: 15,
        height: 40,
        width: 300
    },
    submitContainer: {
        alignItems: 'flex-end'
    },
    searchButton: {
        padding: 10,
        height: 30,
        width: 70,
        alignContent: 'center',
        justifyContent: 'center',
        borderTopEndRadius: 10,
        borderBottomEndRadius: 10,
        marginLeft: -15
    },
    searchInput: {
        borderTopLeftRadius: 10,
        borderBottomLeftRadius: 10,
        margin: 15,
        height: 30,
        borderColor: '#4791bd',
        borderWidth: 1,
        padding: 5,
        flex: 5

    },
    footerButtonHovered: {
        backgroundColor: '#4791bd'
    },
    footerButton: {

    },
    submitButton2: {
        backgroundColor: '#4791bd',
        padding: 10,
        height: 30,
        width: 70,
        alignContent: 'center',
        justifyContent: 'center',
    },
    smallAvatar: {
        width: Platform.OS != 'web' ? 80 : 80,
        height: Platform.OS != 'web' ? 120 : 80,
        flex: 1,
        borderRadius: 20,
        overflow: 'hidden',
        alignSelf: 'center',
        justifyContent: 'center',
    },
    messageAvatar: {
        width: Platform.OS === 'web' ? 50 : 40,
        height: Platform.OS === 'web' ? 50 : 40,
        borderRadius: 20,
    },
    profileAvatar: {
        width: 225,
        height: 225,
        flex: 1,
        borderRadius: 20,
        alignSelf: 'center',
        justifyContent: 'center',
    },
    bigAvatar: {
        width: 120,
        height: 120,
    },
    standardSize: { width: 230, height: 48 },
    wideSize: { width: 312, height: 48 },
    avatarView: {
        flex: 1,
        flexDirection: "row",
        overflow: 'hidden'
    },
    avatarViewClosed: {
        flex: 1,
        flexDirection: "row",
        overflow: 'hidden'
    },
    registerSubmitButton: {
        backgroundColor: '#4791bd',
        padding: 10,
        margin: 15,
        height: 40,
        width: 300
    },
    submitButtonText: {
        color: 'white',
        alignItems: 'center',
        alignContent: 'center',
    },
    informativeText: {
        color: 'black',
        fontSize: 12,
        alignContent: "space-between",
    },
    title: {
        marginBottom: 2,
        fontWeight: 'bold',
        fontSize: 18,
    },
    developmentModeText: {

    },
    contentContainer: {
        flex: 10,
    },
    contentContainer2: {
        flex: 1,
        flexDirection: 'column',
        padding: 50,
        paddingTop: -50
    },
    buttonRow: {
        flexDirection: 'column',
        flex: 2,
        flexWrap: 'wrap',
        flexBasis: 'auto',
        marginRight: 25
    },
    headerContainer: {
        backgroundColor: '#eeeeee',
        padding: 15,
        borderBottomWidth: 1,
        borderColor: 'black'
    },
    chartContainer: {
        flex: 1,
    },
    welcomeContainer: {
        alignItems: 'center',
        paddingTop: 300,
        marginTop: 10,
        marginBottom: 20,
    },
    welcomeImage: {
        height: 150,
        padding: 20,
        margin: 10,
        marginBottom: -20,
        alignSelf: 'stretch',
    },
    customerLogo: {
        height: 35,
        width: Platform.OS != 'web' && bgwidth < 400 ? 140 : 140,
    },
    largeCustomerLogo: {
        height: 150,
        width: Platform.OS != 'web' && bgwidth < 400 ? 80 : 280,
    },
    affitiliateLogo: {
        height: 40,
    },
    Image: {
        height: 275,
    },
    weatherImage: {
        height: Platform.OS === 'web' ? 200 : 100,
        width: Platform.OS === 'web' ? 200 : 150,
    },
    menuLogo: {
        width: 100,
        height: 80,
        marginTop: 5,
        alignItems: 'center',
        alignSelf: 'center',
        alignContent: 'flex-end',
    },
    getStartedContainer: {
        alignItems: 'center',
        marginHorizontal: 50,
    },
    homeScreenFilename: {
        marginVertical: 7,
    },
    codeHighlightText: {
        color: 'rgba(96,100,109, 0.8)',
    },
    codeHighlightContainer: {
        borderRadius: 3,
        paddingHorizontal: 4,
    },
    getStartedText: {
        fontSize: 17,
        lineHeight: 24,
        textAlign: 'center',
        textDecorationStyle: 'solid',
        fontWeight: "bold",
    },
    headerText: {
        fontSize: 17,
        lineHeight: 24,
        textAlign: 'left',
        textDecorationStyle: 'solid',
        fontWeight: "bold",
        marginBottom: 40
    },
    radioButton: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginLeft: -18,
        marginTop: -10,
        borderRadius: 5,
    },
    radioButtonText: {
        marginTop: 7,
        marginLeft: 3,
        flexWrap: 'wrap',
        maxWidth: 250,
        margin: 10
    },
    helpContainer: {
        marginTop: 15,
        marginHorizontal: 20,
        alignItems: 'center',
    },
    helpLink: {
        paddingVertical: 5,
        alignItems: 'center',
        color: 'black'
    },
    helpLinkText: {
        textAlign: 'right',
        fontSize: 24,
    },
    separator: {
        marginVertical: 1,
        height: 3,
        color: 'black',
        opacity: 1,
        flex: 1
    },
    largeSeparator: {
        marginVertical: Platform.OS != 'web' ? 0 : 1,
        height: Platform.OS != 'web' ? 0 : '10%',
        width: Platform.OS != 'web' ? 0 : 1,
        opacity: Platform.OS != 'web' ? 0 : 1,
        flex: 5
    },
    logo: {
        marginTop: 40,
        marginBottom: 10,
        flex: 1,
    },
    link: {
        textDecorationLine: 'underline'
    },
    flagContainer: {
        flex: 1,
        maxHeight: 50
    },
    linkText: {
        fontSize: 14,
        color: '#2e78b7',
    },
    background: {
        zIndex: -1
    },
    gradientBackground: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        height: 300,
        borderRadius: 20,
        paddingHorizontal: 30
    },
    backgroundOverlay: {
        flex: 1,
    },
    Button: {
        paddingVertical: 6,
        margin: 10,
        marginTop: 20,
        backgroundColor: 'rgb(51,199,238)',
        width: 175,
        height: 31,
        textAlign: 'center',
        verticalAlign: "middle",
        shadowColor: 'grey',
        shadowOpacity: 0.4,
        shadowOffset: {
            width: 2.5,
            height: 2.5,
        },
        alignItems: 'center',
        alignContent: 'center',
        borderRadius: 15,
        alignSelf: 'center'
    },
    ButtonHovered: {
        paddingVertical: 6,
        margin: 10,
        marginTop: 20,
        backgroundColor: 'rgb(49,178,210)',
        width: 175,
        height: 31,
        textAlign: 'center',
        verticalAlign: "middle",
        shadowColor: 'grey',
        shadowOpacity: 0.4,
        shadowOffset: {
            width: 2.5,
            height: 2.5,
        },
        alignItems: 'center',
        alignContent: 'center',
        borderRadius: 15,
        alignSelf: 'center'
    },
    ButtonPressed: {
        paddingVertical: 6,
        margin: 10,
        marginTop: 20,
        backgroundColor: 'rgb(51,199,238)',
        width: 175,
        height: 31,
        textAlign: 'center',
        verticalAlign: "middle",
        shadowColor: 'grey',
        shadowOpacity: 0.4,
        shadowOffset: {
            width: 2.5,
            height: 2.5,
        },
        alignItems: 'center',
        alignContent: 'center',
        borderRadius: 15,
        alignSelf: 'center'
    },
    LoginButton: {
        paddingVertical: 6,
        borderColor: 'rgb(51,199,238)',
        borderWidth: 2,
        maxHeight: 45,
        textAlign: 'center',
        verticalAlign: "middle",
        justifyContent: 'center',
        shadowColor: 'grey',
        shadowOpacity: 0.4,
        
        shadowRadius: 2,
        shadowOffset: {
            width: 2.5,
            height: 2.5,
        },
        alignItems: 'center',
        alignContent: 'center',
        borderRadius: 10,
        alignSelf: 'stretch',
        flex: 1,
        minHeight: 40,
        backgroundColor: '#ececec'
    },
    LoginButtonDisabled: {
        paddingVertical: 6,
        margin: 15,
        height: 50,
        backgroundColor: 'grey',
        width: 175,
        textAlign: 'center',
        verticalAlign: "middle",
        shadowColor: 'grey',
        shadowOpacity: 0.4,
        
        shadowRadius: 5,
        shadowOffset: {
            width: 2.5,
            height: 2.5,
        },
        alignItems: 'center',
        alignContent: 'center',
        borderRadius: 15,
        alignSelf: 'center',
        opacity: 0.7,
        justifyContent: 'center'
    },
    LoginButtonHovered: {
        paddingVertical: 6,
        margin: 15,
        backgroundColor: 'grey',
        borderColor: 'rgb(51,199,238)',
        borderWidth: 2,
        maxHeight: 50,
        textAlign: 'center',
        verticalAlign: "middle",
        justifyContent: 'center',
        shadowColor: 'grey',
        shadowOpacity: 0.4,
        
        shadowRadius: 20,
        shadowOffset: {
            width: 2.5,
            height: 2.5,
        },
        alignItems: 'center',
        alignContent: 'center',
        borderRadius: 30,
        alignSelf: 'stretch',
        flex: 1,
        minHeight: 40
    },
    LoginButtonHoveredMobile: {
        paddingVertical: 6,
        margin: 15,
        backgroundColor: 'rgb(51,199,140)',
        width: 175,
        minHeight: 31,
        textAlign: 'center',
        verticalAlign: "middle",
        shadowColor: 'grey',
        shadowOpacity: 0.6,
        
        shadowOffset: {
            width: 2.5,
            height: 2.5,
        },
        alignItems: 'center',
        alignContent: 'center',
        borderRadius: 15,
        alignSelf: 'center',
        flex: 1
    },
    LogoutButton: {
        paddingVertical: 6,
        margin: 15,
        backgroundColor: 'rgb(204,0,51)',
        width: 175,
        height: 31,
        textAlign: 'center',
        verticalAlign: "middle",
        shadowColor: 'grey',
        shadowOpacity: 0.6,
        
        shadowOffset: {
            width: 2.5,
            height: 2.5,
        },
        alignItems: 'center',
        alignContent: 'center',
        borderRadius: 15,
        alignSelf: 'center',
        flex: 1
    },
    LogoutButtonHovered: {
        paddingVertical: 6,
        margin: 15,
        backgroundColor: 'rgb(204,51,51)',
        width: 175,
        height: 31,
        textAlign: 'center',
        verticalAlign: "middle",
        shadowColor: 'grey',
        shadowOpacity: 0.6,
        
        shadowOffset: {
            width: 2.5,
            height: 2.5,
        },
        alignItems: 'center',
        alignContent: 'center',
        borderRadius: 15,
        alignSelf: 'center',
    },
    BackToLogin: {
        paddingVertical: 6,
        margin: 15,
        backgroundColor: 'grey',
        height: 50,
        textAlign: 'center',
        verticalAlign: "middle",
        shadowColor: 'grey',
        shadowOpacity: 0.4,
        
        shadowOffset: {
            width: 2.5,
            height: 2.5,
        },
        alignItems: 'center',
        alignContent: 'center',
        borderRadius: 15,
        alignSelf: 'stretch',
        opacity: 0.7,
        justifyContent: 'center'
    },
    checkbox: {
        margin: 8,
    },
    Button2: {
        padding: 25,
        margin: 15,
        backgroundColor: 'rgb(51,199,238)',
        width: 175,
        height: 20,
        textAlign: 'center',
        verticalAlign: "middle",
        shadowColor: 'grey',
        shadowOpacity: 0.4,
        shadowOffset: {
            width: 2.5,
            height: 2.5,
        },
        alignItems: 'flex-end',
        alignContent: 'flex-end',
        alignSelf: 'flex-end'
    },
    appButtonText: {
        fontSize: 14,
        fontWeight: "bold",
        textAlign: 'center',
        alignContent: 'center',
        alignSelf: 'center',
        color: 'white',
    },
    appButtonText2: {
        fontSize: 14,
        fontWeight: "bold",
        textAlign: 'center',
        alignContent: 'center',
        alignSelf: 'center',
    },
    //TODO #19 add dynamicity
    menuOpen: {
        alignContent: 'flex-start',
        flex: 1,
        zIndex: 1,
    },
    menuClosed: {
        alignContent: 'flex-start',
        flex: .3,
        zIndex: 1
    },
    avatarContainer: {
        margin: 25,
        marginLeft: 30,
        flex: 1
    },
    avatar: {
        width: 130,
        height: 130,
        borderRadius: 50,
        borderWidth: 2,
        borderColor: 'white',
        shadowOffset: {
            width: 2,
            height: 2,
        },
        shadowOpacity: 0.4,
        shadowColor: 'black',
        margin: 25,
        marginLeft: 50,
    },
    name: {
        position: 'absolute',
        left: 100,
        fontSize: 16,
        fontWeight: 'bold',
        color: 'black',
    },
    name2: {
        position: 'relative',
        left: 100,
        fontSize: 14,
        color: 'black',
        marginTop: -20,
        paddingRight: 25,
    },
    description: {
        position: 'relative',
        top: 40,
        left: 90,
        fontSize: 10,
    },
    item: {
        fontSize: 14,
        fontWeight: '300',
        paddingTop: 5,
    },
    welcome: {
        fontSize: 20,
        textAlign: 'center',
        margin: 10,
        marginBottom: 40,
    },
    instructions: {
        textAlign: 'center',
        color: '#333333',
        marginBottom: 5,
    },
    linearGradient: {
        paddingLeft: 15,
        paddingRight: 15,
        height: bgheight,
    },
    screen: {
        flex: 1,
        padding: 20,
    },
    menuItem: {
        padding: 5,
        margin: 2,
        minWidth: 60,
        marginVertical: .5,
        flex: 1,
        borderLeftColor: '#rgb(90,192,234)',
        flexWrap: "nowrap",
        left: -12,
        borderLeftWidth: 8,
        alignItems: 'center',
        alignSelf: 'stretch',
    },
    menuItemSelected: {
        padding: 5,
        left: -12,
        minWidth: 60,
        margin: 2,
        marginVertical: .5,
        flex: 1,
        borderColor: '#ffffff4D',
        flexWrap: "nowrap",
        alignItems: 'center',
        alignSelf: 'stretch',
    },
    menuHovered: {
        padding: 5,
        margin: 2,
        minWidth: 60,
        marginVertical: .5,
        flex: 1,
        backgroundColor: 'rgb(90,192,234)',
        shadowRadius: 10,
        shadowOpacity: 0.2,
        shadowOffset: {
            width: 0,
            height: 3,
        },
        alignItems: 'center',
    },
    menuHideShow: {
        padding: 5,
        backgroundColor: '#eeeeee',
        borderRadius: 50,
        shadowColor: 'black',
        shadowOpacity: 0.4,
        shadowRadius: 8,
        textAlign: 'center',
        color: 'grey',
    },
    menuHideShow2: {
        padding: 5,
        backgroundColor: '#eeeeee',
        borderRadius: 50,
        shadowColor: 'grey',
        shadowOpacity: 0.4,
        shadowRadius: 8,
        shadowOffset: {
            width: 3,
            height: 0,
        },
        textAlign: 'center',
        verticalAlign: "middle",
        color: 'grey',
        marginTop: 40,
        marginLeft: -10
    },
    menuMarginOpen: {
        flexDirection: 'column',
        height: 40,
        marginLeft: 100,
        position: 'absolute',
        zIndex: 1,
        textAlign: 'center',
        alignContent: 'center',
        alignItems: 'center',
    },
    menuMarginClosed: {
        flex: 1,
        height: 40,
        marginLeft: 200,
        position: 'absolute'
    },
    menuItemIcon: {
        height: 20,
        width: 20,
        textAlign: 'center',
        verticalAlign: "middle",
        justifyContent: 'center',
    },
    buttonIcon: {
        marginLeft: 20,
        marginTop: -13,
        paddingTop: 4,
        height: 40,
        width: 40,
        borderRadius: 7,
        borderTopEndRadius: 7,
        borderTopLeftRadius: 7,
        textAlign: 'left',
        verticalAlign: "middle",
        color: '#eeeeee',
        overflow: 'hidden'
    },
    footerButtonIcon: {
        paddingTop: 4,
        marginTop: -4,
        marginHorizontal: 4,
        height: 40,
        width: 40,
        borderRadius: 7,
        borderTopEndRadius: 7,
        borderTopLeftRadius: 7,
        textAlign: 'left',
        verticalAlign: "middle",
        color: '#eeeeee',
        overflow: 'hidden'
    },
    tab: {
        marginTop: -15,
        borderTopLeftRadius: 15,
        borderTopRightRadius: 15,
        borderColor: 'black',
        paddingHorizontal: 15,
        paddingVertical: 10,
        backgroundColor: '#8BBEDC',
        
        shadowRadius: 5,
        shadowOpacity: .2,
        flex: 1,
        alignContent: 'center',
        flexDirection: 'row',
        flexWrap: 'nowrap'
    },
    activetab: {
        marginTop: -15,
        borderTopLeftRadius: 15,
        borderTopRightRadius: 15,
        borderColor: 'black',
        paddingHorizontal: 15,
        paddingVertical: 10,
        backgroundColor: '#5fb561ff',
        
        flex: 1,
        alignContent: 'center',
        flexDirection: 'row',
        flexWrap: 'nowrap',
    },
    tabInactive: {
        flex: 1,
        margin: 3,
        flexDirection: 'row'
    },
    tabHovered: {
        flex: 1,
        margin: 3,
        flexDirection: 'row',
        backgroundColor: '#5fb561ff',
        paddingHorizontal: 15,
        paddingVertical: 10,
        borderTopLeftRadius: 15,
        borderTopRightRadius: 15,
        
        shadowRadius: 20,
        shadowOpacity: 1,
        shadowColor: 'white',
        marginTop: -25
    },
    editAvatarButton: {
        paddingTop: 4,
        height: 40,
        width: 40,
        backgroundColor: 'white',
        borderRadius: 7,
        borderTopEndRadius: 7,
        borderTopLeftRadius: 7,
        shadowColor: 'grey',
        shadowOpacity: 0.4,
        shadowOffset: {
            width: 3,
            height: 3,
        },
        textAlign: 'center',
        verticalAlign: "middle",
        color: 'grey',
        zIndex: 1,
        position: 'absolute',
        overflow: 'hidden'
    },
    statusBarIcon: {
        minHeight: 30,
        width: 30,
        textAlign: 'center',
        verticalAlign: "middle",
        justifyContent: 'center',
        color: 'black',
        overflow: 'hidden',
        paddingTop: 5
    },
    triangle: {
        width: 2,
        height: 2,
        borderLeftWidth: 50,
        borderRightWidth: 50,
        borderBottomWidth: 100,
        borderStyle: 'solid',
        borderBottomColor: 'green',
        backgroundColor: 'transparent',
        borderLeftColor: 'transparent',
        borderRightColor: 'transparent',
    },
    menuItemIconSelected: {
        marginLeft: 20,
        marginTop: -29,
        paddingTop: 4,
        height: 40,
        width: 40,
        backgroundColor: 'white',
        borderRadius: 7,
        borderTopEndRadius: 7,
        borderTopLeftRadius: 7,
        shadowColor: 'grey',
        shadowOpacity: 0.4,
        shadowOffset: {
            width: 3,
            height: 3,
        },
        textAlign: 'center',
        verticalAlign: "middle",
        color: 'black',
        overflow: 'hidden'
    },
    menuItemText: {
        marginTop: 4,
        fontSize: 14,
        flex: 0.01,
        fontWeight: '500',
        color: 'white',
        overflow: 'visible',
    },
    fieldItemText: {
        fontSize: 18,
        marginBottom: 10,
        marginTop: 10,
        fontWeight: '600',
        textAlign: 'left',
    },
    date: {
        fontSize: 14,
        fontWeight: '600',
        verticalAlign: "middle",
        textAlign: 'left',
        padding: 6,
        shadowColor: 'grey',
        shadowOpacity: 0.4,
        shadowOffset: {
            width: 3,
            height: 3,
        },
        
        borderRadius: 5,
        zIndex: 2,
        backgroundColor: 'white'
    },
    footerTabs: {
        flex: .05,
        zIndex: -4,
        marginTop: -10,
        alignItems: 'center'
    },
    headerSection: {

    },
    pageContainer: {
        flexDirection: 'column',
        flex: 1,
        paddingTop: Platform.OS === 'web' ? StatusBar.currentHeight : 10,
    },
    innerContainer: {
        paddingLeft: Platform.OS === 'web' ? 30 : 0,
    },
    appContainer: {
        flex: 11,
    },
    loginContainer: {
        flex: 1,
    },
    registrationContainer: {
        flex: 1,
        flexDirection: 'row',
        alignContent: 'flex-start',
    },
    dashContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-around',
        alignContent: 'space-around',
    },
    cardContainerAlt: {
        flexDirection: 'column'
    },
    settingsCardBack: {
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    cardContainer: {
        flex: .7,
        flexDirection: 'row',
        margin: 5,
        alignSelf: 'center',
        alignContent: 'center',
        alignItems: 'center',
        backgroundColor: '#eeeeee',
        shadowColor: 'black',
        
        shadowOpacity: 0.4,
        borderRadius: 15,
        padding: 80,
        minWidth: 300
    },
    searchBarContainer: {
        flex: 1,
        flexDirection: 'row',
        margin: 30,
        marginBottom: -20,
        alignSelf: 'center',
        alignContent: 'center',
        alignItems: 'center',
        justifyContent: 'flex-end',
        backgroundColor: 'white',
        shadowColor: 'black',
        
        shadowOpacity: 0.4,
        borderRadius: 15,
        padding: 10,
        minWidth: 300,
        overflow: 'hidden'

    },
    cardContainer2: {
        flex: 1,
        flexDirection: 'column',
        margin: 5,
        alignSelf: 'stretch',
        alignContent: 'center',
        alignItems: 'center',
        shadowColor: 'black',
        backgroundColor: '#eeeeee',
        // 
        // shadowOpacity: 0.4,
        borderRadius: 15,
        padding: 15,
        minWidth: 300,
        maxHeight: 500,
    },
    cardContainer3: {
        flex: 1,
        flexDirection: 'column',
        margin: 5,
        alignSelf: 'stretch',
        alignContent: 'center',
        alignItems: 'center',
        shadowColor: 'black',
        backgroundColor: '#eeeeee',
        
        shadowOpacity: 0.4,
        borderRadius: 15,
        padding: 15,
        minWidth: 280
    },
    cardContainer4: {
        flex: 2,
        width: '100%',
        flexDirection: 'row',
        margin: 5,
        alignSelf: 'stretch',
        alignContent: 'center',
        alignItems: 'center',
        shadowColor: 'black',
        
        backgroundColor: '#eeeeee',
        shadowOpacity: 0.4,
        borderRadius: 15,
        padding: 15,
        overflow: 'hidden'
    },
    cardContainer5: {
        flex: 1,
        flexDirection: 'column',
        margin: 5,
        // alignSelf: 'stretch',
        alignContent: 'center',
        alignItems: 'center',
        shadowColor: 'black',
        
        shadowOpacity: 0.4,
        borderRadius: 15,
        padding: 15,
        maxHeight: 350
    },
    card: {
        width: '100%',
        flex: 1,
    },
    card1: {
        width: '100%',
        flex: 1,
    },
    card2: {
        flex: 1,
        alignContent: 'center',
        alignSelf: 'center',
    },
    noteContainer: {
        flex: 1,
        flexDirection: 'column',
        alignSelf: 'center',
        alignContent: 'center',
        alignItems: 'center',
        shadowColor: 'black',
        
        borderRadius: 10,
        flexWrap: 'wrap',
    },
    smallWidgetContainer: {
        flex: 1,
        minWidth: 300,
        alignSelf: 'center',
        
        padding: 5,
        top: 3,
        borderTopEndRadius: 20,
        borderTopStartRadius: 20
    },
    noteContainerNoShadow: {
        flex: 1,
        flexDirection: 'column',
        alignSelf: 'center',
        alignContent: 'center',
        alignItems: 'center',
        backgroundColor: '#eeeeee',
        shadowColor: 'black',
        borderRadius: 10,
        minWidth: 325,
        flexWrap: 'wrap',
    },
    rounderShadowContainer: {
        flex: 1,
        flexDirection: 'column',
        alignContent: 'center',
        alignItems: 'center',
        backgroundColor: '#eeeeee',
        shadowColor: 'black',
        
        shadowRadius: 10,
        shadowOpacity: 0.4,
        borderRadius: 10,
        flexWrap: 'wrap',
    },
    captureCardContainer: {
        flex: 1,
        backgroundColor: '#eeeeee',
        flexDirection: 'column'
    },
    standardCard: {
        padding: 20,
        backgroundColor: '#eeeeee',
        alignItems: 'stretch',
    },
    // Professional card styles for profile and other components
    profileCard: {
        backgroundColor: '#ffffff',
        borderRadius: 16,
        padding: 24,
        marginBottom: 16,
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 4
        },
        shadowOpacity: 0.08,
        shadowRadius: 40,
        
        borderWidth: Platform.OS === 'web' ? 1 : 0,
        borderColor: 'rgba(0, 0, 0, 0.05)',
    },
    profileCardDark: {
        backgroundColor: '#2a2a2a',
        borderRadius: 16,
        padding: 24,
        marginBottom: 16,
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 4
        },
        shadowOpacity: 0.15,
        shadowRadius: 12,
        
        borderWidth: Platform.OS === 'web' ? 1 : 0,
        borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    infoCard: {
        backgroundColor: '#ffffff',
        borderRadius: 12,
        padding: 20,
        marginBottom: 12,
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 2
        },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        
        borderWidth: Platform.OS === 'web' ? 1 : 0,
        borderColor: 'rgba(0, 0, 0, 0.03)',
    },
    infoCardDark: {
        backgroundColor: '#333333',
        borderRadius: 12,
        padding: 20,
        marginBottom: 12,
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 2
        },
        shadowOpacity: 0.12,
        shadowRadius: 8,
        
        borderWidth: Platform.OS === 'web' ? 1 : 0,
        borderColor: 'rgba(255, 255, 255, 0.08)',
    },
    spacer: {
        margin: 15,
        paddingBottom: 100,
        marginBottom: 250,
    },
    interactiveContentContainer: {
        alignContent: 'center',
        height: bgheight
    },
    errorTextStyle: {
        fontWeight: 'bold',
        color: 'red',
        fontSize: 14,
    },
    dataSheet: {
        flex: 1,
        flexDirection: Platform.OS != 'web' ? 'column' : 'row',
        flexWrap: 'wrap',
        alignSelf: 'flex-start',
        padding: 25
    },
    dataSheet2: {
        flex: 1,
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignSelf: 'center',
    },
    dataEnquirySmall: {
        flex: 1,
        flexDirection: 'row',
        padding: 1,
        flexWrap: 'wrap',
        minWidth: 200
    },
    dataEnquiryMedium: {
        flex: 1,
        flexDirection: 'row',
        padding: 1,
        flexWrap: 'wrap',
        minWidth: 300,
        alignContent: 'flex-start'
    },
    dataEnquiry1: {
        flex: 2,
        flexDirection: 'column',
        padding: '1%',
        flexWrap: 'wrap',
        // margin: 35
    },
    dataEnquiry2: {
        flex: 2,
        flexDirection: 'column',
        padding: '5%',
        flexWrap: 'wrap'
    },
    dataEnquiry3: {
        flex: 1,
        flexDirection: 'column',
        padding: '5%',
        flexWrap: 'wrap'
    },
    dataEnquiry4: {
        flex: 1,
        flexDirection: 'column',
        padding: '5%',
        flexWrap: 'wrap',
    },
    searchFields: {
        flex: 1,
        alignContent: 'flex-start',
    },
    modalContainer: {
        alignItems: 'center',
        flex: 1,
        justifyContent: 'center'
    },
    modalContaineralt: {
        alignItems: 'center',
        borderRadius: 15,
        zIndex: 99999,
        justifyContent: 'center',
        margin: 'auto',
        padding: 30,
        width: 300,
        
        shadowOpacity: 0.4,
        overflow: 'hidden',
        flex: .4,
        flexBasis: 'auto',
        backgroundColor: 'white'
    },
    modalContainerAuth: {
        alignItems: 'center',
        borderRadius: 15,
        justifyContent: 'center',
        alignSelf: 'center',
        padding: 5,
        backgroundColor:"white",
        
        shadowRadius: 20,
        shadowOpacity: 0.2,
        overflow: 'hidden',
        flex: 1,
        flexBasis: 'auto',
        zIndex: 99999,
        maxHeight: 700
    },
    modalContainerHelp: {
        alignItems: 'center',
        backgroundColor: '#eeeeee',
        borderColor: 'white',
        borderRadius: 20,
        borderWidth: .4,
        justifyContent: 'center',
        height: 300,
        margin: 'auto',
        padding: 30,
        width: 300,
        shadowColor: 'black',
        
        shadowOpacity: 0.4,
        overflow: 'hidden',
        position: 'absolute'
    },
    modalContainerLanguage: {
        alignSelf: Platform.OS != 'web' ? 'center' : 'flex-end',
        alignContent: 'flex-start',
        padding: Platform.OS != 'web' ? 0 : 15,
        borderBottomLeftRadius: 20,
        borderBottomRightRadius: 20,
        
        shadowRadius: 20,
        shadowOpacity: 0.4,
        justifyContent: 'flex-start',
        flex: 1,
        width: 400,
        backgroundColor: '#eeeeee',
        bottom: 0,
        marginTop: Platform.OS != 'web' ? -950 : 0,
        marginLeft: Platform.OS != 'web' ? -100 : 0,
        minHeight: 600,
        maxHeight: 600,
        flexDirection: 'column'
    },
    modalContainerNotifications: {
        padding: 15,
        borderBottomLeftRadius: 20,
        borderBottomRightRadius: 20,
        
        shadowOpacity: 0.4,
        flex: 1,
        backgroundColor: '#eeeeee',
        minHeight: 600,
        maxHeight: 600,
        minWidth: '80%',
        flexDirection: 'column',
        alignSelf: 'center'
    },
    modalContainerMessage: {
        alignSelf: 'flex-end',
        alignContent: 'flex-end',
        justifyContent: 'flex-start',
        flex: 0.1,
        borderRadius: 20,
        
        shadowOpacity: 0.4,
        minWidth: 465,
        minHeight: 600,
        maxHeight: 600,
        backgroundColor: '#eeeeee',
        marginTop: -855,
        position: 'absolute'
    },
    appInfoContainer: {
        alignItems: 'center',
        backgroundColor: '#eeeeee',
        justifyContent: 'center',
        borderRadius: 20,
        marginHorizontal: bgwidth / 3.5,
        shadowColor: 'black',
        
        shadowOpacity: 0.4
    },
    appLoadingContainer: {
        flex: 5,
        alignItems: 'center',
        alignSelf: 'center',
        justifyContent: 'center',
        alignContent: 'center',
        width: 600,
        backgroundColor: '#eeeeee',
        borderRadius: 20,
        
        shadowOpacity: 0.4,
        padding: 50
    },
    modalInfoContainer: {
        alignItems: 'stretch',
        backgroundColor: '#eeeeee',
        alignSelf: 'center',
        height: 800,
        borderRadius: 20,
        width: bgwidth / 2,
        shadowColor: 'black',
        
        shadowOpacity: 0.4,
        padding: 40,
        margin: 40
    },
    modalInfoContainerMobile: {
        alignItems: 'stretch',
        backgroundColor: '#eeeeee',
        alignSelf: 'center',
        height: 800,
        borderRadius: 20,
        width: bgwidth / 1.1,
        shadowColor: 'black',
        
        shadowOpacity: 0.4,
        padding: 10,
        margin: 10
    },
    loginMobile: {
        marginTop: bgwidth < 400 ? '20%' : 50,
        marginBottom: '20%',
        flex: 1
    },
    fullScreenModal: {
        alignItems: 'stretch',
        backgroundColor: '#eeeeee',
        alignSelf: 'center',
        height: bgheight,
        borderRadius: 20,
        width: bgwidth,
        shadowColor: 'black',
        
        shadowOpacity: 0.4,
        padding: 40,
        justifyContent: 'space-around'
    },
    addUserModal: {
        marginTop: -2,
        alignItems: 'stretch',
        backgroundColor: '#eeeeee',
        alignSelf: 'center',
        height: bgheight / 1.3,
        borderRadius: 20,
        width: bgwidth / 3,
        shadowColor: 'black',
        
        shadowOpacity: 0.4,
        padding: 40,
        justifyContent: 'space-around',
        maxWidth: 1000
    },
    blurViewStyle: {
        position: 'absolute', bottom: -3000, top: -3000, left: -3000, right: -3000, opacity: 1
    },
    addUserModalMobile: {
        marginTop: -2,
        alignItems: 'stretch',
        backgroundColor: '#eeeeee',
        alignSelf: 'center',
        height: bgheight / 1.3,
        borderRadius: 20,
        width: bgwidth - 10,
        shadowColor: 'black',
        
        shadowOpacity: 0.4,
        padding: 40,
        justifyContent: 'space-around'
    },
    fullScreenModalBackground: {
        alignItems: 'stretch',
        alignSelf: 'center',
        height: bgheight,
        width: bgwidth,
        justifyContent: 'space-around'
    },
    gap: {
        height: bgheight / 19
    },
    headerBanner: {
        color: 'black',
        fontSize: 24,
        paddingLeft: 30,
        paddingBottom: 8,
        paddingTop: 3,
        fontWeight: '600',
        textAlign: 'left',
        alignContent: 'center',
        marginVertical: 15
    },
    cardBig: {
        borderRadius: 20,
        flex: 1
    },
    defaultCardContainer: {
        borderRadius: 15,
        padding:10
    },

    // Modern Glassmorphism Design System
    // Main Container Styles
    modernCard: {
        borderRadius: 24,
        padding: 20,
        
        marginVertical: 8,
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(20px)',
            borderWidth: 1,
            borderColor: 'rgba(0,0,0,0.1)',
        }),
    },
    modernCardSmall: {
        borderRadius: 20,
        padding: 16,
        marginVertical: 6,
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(16px)',
            borderWidth: 1,
            borderColor: 'rgba(0,0,0,0.1)',
        }),
    },
    modernCardLarge: {
        borderRadius: 32,
        padding: 24,
        marginVertical: 12,
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(24px)',
            borderWidth: 1,
            borderColor: 'rgba(0,0,0,0.1)',
        }),
    },

    // Background Colors (Light/Dark Theme)
    modernBgLight: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
    },
    modernBgDark: {
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
    },
    modernBgLightSubtle: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
    modernBgDarkSubtle: {
        backgroundColor: 'rgba(30, 41, 59, 0.9)',
    },

    // Section Containers
    modernSection: {
        marginBottom: 20,
    },
    modernSectionLarge: {
        marginBottom: 32,
    },
    modernMainContent: {
        gap: 20,
        paddingHorizontal: Platform.select({ web: 20 }),
    },
    modernGrid: {
        flex: 1,
        gap: 16,
        paddingHorizontal: 20,
        paddingVertical: 16,
    },

    // Typography Styles
    modernSectionTitle: {
        color: '#1e293b',
        marginBottom: 8,
        fontSize: webResponsiveFontSize(24),
        fontWeight: '700',
    },
    modernPanelTitle: {
        marginBottom: 20,
        color: '#1e293b',
        fontSize: webResponsiveFontSize(16),
        fontWeight: '600',
    },
    modernCardTitle: {
        marginBottom: 4,
        fontSize: webResponsiveFontSize(16),
        fontWeight: '600',
    },
    modernFieldLabel: {
        fontSize: webResponsiveFontSize(12),
        fontWeight: '600',
        marginBottom: 4,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    modernFieldValue: {
        fontSize: webResponsiveFontSize(16),
        fontWeight: '500',
        lineHeight: 22,
    },
    modernCautionText: {
        lineHeight: 22,
        padding: 20,
        borderRadius: 16,
        backgroundColor: 'rgba(0,0,0,0.02)',
    },

    // Button Styles
    modernButton: {
        minHeight: webResponsiveHeight(48),
        borderRadius: 16,
        paddingHorizontal: webResponsivePadding(20),
        shadowColor: '#3b82f6',
        alignSelf:"stretch",
        minWidth:"100%",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 12,
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    modernActionButton: {
        width: webResponsiveHeight(44),
        height: webResponsiveHeight(44),
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(8px)',
            transition: 'all 0.2s ease',
        }),
    },
    modernIconButton: {
        width: webResponsiveHeight(36),
        height: webResponsiveHeight(36),
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
    },

    // Layout Containers
    modernRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
    },
    modernColumn: {
        gap: 12,
    },
    modernInfoRow: {
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 12,
        backgroundColor: 'rgba(0,0,0,0.02)',
        marginVertical: 2,
    },

    // Profile/Avatar Styles
    modernProfileImage: {
        width: Platform.select({ web: 120, default: 100 }),
        height: Platform.select({ web: 120, default: 100 }),
        borderRadius: 20,
    },
    modernAvatarContainer: {
        width: Platform.select({ web: 120, default: 100 }),
        height: Platform.select({ web: 120, default: 100 }),
        borderRadius: 20,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderColor: 'rgba(59, 130, 246, 0.3)',
    },
    modernImageContainer: {
        borderRadius: 20,
        overflow: 'hidden',
        
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
    },

    // List Styles
    modernHorizontalList: {
        paddingHorizontal: 20,
        gap: 16,
    },
    modernListCard: {
        minWidth: 280,
        height: 140,
        borderRadius: 24,
        padding: 20,
        marginHorizontal: 8,
        
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.12,
        shadowRadius: 24,
        justifyContent: 'space-between',
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }),
    },

    // Stats/Info Cards
    modernStatsCard: {
        borderRadius: 20,
        padding: 20,
        height: 120,
        
        marginVertical: 8,
        justifyContent: 'center',
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(16px)',
            transition: 'all 0.3s ease',
        }),
    },
    modernStatsNumber: {
        textAlign: 'right',
        alignSelf: 'flex-end',
        marginTop: 'auto',
        fontSize: webResponsiveFontSize(24),
        fontWeight: '700',
    },
    modernCardIcon: {
        marginBottom: 8,
        alignSelf: 'flex-start',
    },

    // Animation Containers
    modernAnimationContainer: {
        flex: 1,
        maxHeight: 220,
        alignItems: 'center',
        justifyContent: 'center',
    },

    // Side Panel Styles
    modernSidePanel: {
        flex: 1,
        maxWidth: 300,
    },
    modernMainSection: {
        flex: 4,
        zIndex: 1,
    },

    // Color Variations for Icons/Actions - Glass Morphism
    modernActionGreen: {
        backgroundColor: 'rgba(34, 197, 94, 0.15)',
        borderColor: 'rgba(34, 197, 94, 0.5)',
        borderWidth: 1,
        shadowColor: 'rgba(34, 197, 94, 0.3)',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
        
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(10px)',
        }),
    },
    modernActionBlue: {
        backgroundColor: 'rgba(59, 130, 246, 0.65)',
        borderColor: 'rgba(59, 130, 246, 0.5)',
        borderWidth: 1,
        shadowColor: 'rgba(59, 130, 246, 0.3)',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
        
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(10px)',
        }),
    },
    modernActionRed: {
        backgroundColor: 'rgba(245, 101, 101, 0.4)',
        borderColor: 'rgba(245, 101, 101, 0.5)',
        borderWidth: 1,
        shadowColor: 'rgba(245, 101, 101, 0.3)',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
        
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(10px)',
        }),
    },
    modernActionAmber: {
        backgroundColor: 'rgba(245, 158, 11, 0.4)',
        borderColor: 'rgba(245, 158, 11, 0.5)',
        borderWidth: 1,
        shadowColor: 'rgba(245, 158, 11, 0.3)',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
        
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(10px)',
        }),
    },
    modernActiongrey: {
        backgroundColor: 'rgba(148, 163, 184, 0.15)',
        borderColor: 'rgba(148, 163, 184, 0.5)',
        borderWidth: 1,
        shadowColor: 'rgba(148, 163, 184, 0.3)',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
        
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(10px)',
        }),
    },

    // Dark Mode Variants - Glass Morphism
    modernActionGreenDark: {
        backgroundColor: 'rgba(34, 197, 94, 0.25)',
        borderColor: 'rgba(34, 197, 94, 0.6)',
        borderWidth: 1,
        shadowColor: 'rgba(34, 197, 94, 0.4)',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.4,
        shadowRadius: 12,
        
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(10px)',
        }),
    },
    modernActionBlueDark: {
        backgroundColor: 'rgba(59, 130, 246, 0.25)',
        borderColor: 'rgba(59, 130, 246, 0.6)',
        borderWidth: 1,
        shadowColor: 'rgba(59, 130, 246, 0.4)',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.4,
        shadowRadius: 12,
        
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(10px)',
        }),
    },
    modernActionRedDark: {
        backgroundColor: 'rgba(245, 101, 101, 0.25)',
        borderColor: 'rgba(245, 101, 101, 0.6)',
        borderWidth: 1,
        shadowColor: 'rgba(245, 101, 101, 0.4)',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.4,
        shadowRadius: 12,
        
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(10px)',
        }),
    },
    modernActionAmberDark: {
        backgroundColor: 'rgba(245, 158, 11, 0.25)',
        borderColor: 'rgba(245, 158, 11, 0.6)',
        borderWidth: 1,
        shadowColor: 'rgba(245, 158, 11, 0.4)',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.4,
        shadowRadius: 12,
        
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(10px)',
        }),
    },
    modernActiongreyDark: {
        backgroundColor: 'rgba(148, 163, 184, 0.25)',
        borderColor: 'rgba(148, 163, 184, 0.6)',
        borderWidth: 1,
        shadowColor: 'rgba(148, 163, 184, 0.4)',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.4,
        shadowRadius: 12,
        
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(10px)',
        }),
    },

    // Static Menu Header Styles
    staticMenuHeader: {
        alignSelf: 'center',
        justifyContent: 'center',
        borderRadius: 28,
        paddingHorizontal: 5,
        margin: 8,
        marginTop: 0,
        
    },
    staticMenuHeaderLight: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: 'rgba(0, 0, 0, 0.08)',
        shadowOpacity: 0.1,
    },
    staticMenuHeaderDark: {
        backgroundColor: 'rgba(40, 40, 40, 0.95)',
        borderColor: 'rgba(255, 255, 255, 0.12)',
        shadowOpacity: 0.3,
    },
    staticMenuXStack: {
        columnGap: 8,
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
    },
    staticMenuPressable: {
        minWidth: Platform.select({web: 45, default: 20}),
        minHeight: 45,
        padding: 8,
        borderRadius: 20,
        marginVertical: 4,
        ...(Platform.OS === 'web' && {
            transition: 'all 0.2s ease',
        }),
    },
    staticMenuPressableOpen: {
        flex: 1,
        minWidth: Platform.select({ native: 40, web: 40 }),
        borderWidth: Platform.select({ native: 1, web: 0 }),
        borderColor: "white"
    },
    staticMenuPressableClosed: {
        flex: 0,
    },
    staticMenuPressableHoveredLight: {
        backgroundColor: 'rgba(90, 192, 234, 0.15)',
    },
    staticMenuPressableHoveredDark: {
        backgroundColor: 'rgba(90, 192, 234, 0.25)',
    },
    staticMenuIconContainer: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    staticMenuIconWrapper: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
    },
    staticMenuIconWrapperLight: {
        backgroundColor: 'rgba(90, 192, 234, 0.1)',
    },
    staticMenuIconWrapperDark: {
        backgroundColor: 'rgba(90, 192, 234, 0.2)',
    },
    staticMenuLargeIconWrapper: {
        width: 48,
        height: 48,
        borderRadius: 24,
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'visible',
    },
    staticMenuText: {
        fontSize: 11,
        fontWeight: '500',
        textAlign: 'center',
        marginTop: 4,
    },
    staticMenuTextLight: {
        color: '#333333',
    },
    staticMenuTextDark: {
        color: 'white',
    },
    staticMenuDropdown: {
        minWidth: 200,
        borderRadius: 12,
        borderWidth: 1,
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(10px)',
        }),
    },
    staticMenuDropdownLight: {
        backgroundColor: 'white',
        borderColor: '#e0e0e0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    },
    staticMenuDropdownDark: {
        backgroundColor: '#333333',
        borderColor: '#555555',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    },
    staticMenuDropdownItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        paddingVertical: 12,
    },
    staticMenuDropdownItemLight: {
        color: '#333333',
    },
    staticMenuDropdownItemDark: {
        color: 'white',
    },

    // Enhanced Login Screen Styles for better vertical layout
    loginMainCard: {
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderRadius: 24,
        
        flex: 1,
        alignSelf: "center",
    },

    loginAnimationContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 80,
        flex: 1,
        maxHeight: 140,
        paddingVertical: 8,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'transparent',
    },

    loginAnimationLottie: {
        flex: 1,
        width: "100%",
        height: "100%",
        borderRadius: 16,
        backgroundColor: 'transparent',
        position: 'relative',
    },

    loginFormContainer: {
        flex: 1,
    },

    loginSocialSection: {
        justifyContent: 'center',
        borderRadius: 20,
        flex: 1,
        minHeight: 200,
        paddingVertical: 16,
        paddingHorizontal: 12,
    },

    loginSocialDividerContainer: {
        alignSelf: "stretch",
        alignItems: "center",
        marginVertical: 8,
    },

    loginSocialDividerLine: {
        flex: 1,
        height: 2,
        backgroundColor: 'rgba(148, 163, 184, 0.3)',
        borderRadius: 1,
    },

    loginSocialDividerText: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: 20,
        paddingVertical: 6,
        paddingHorizontal: 16,
        borderWidth: 1,
        borderColor: 'rgba(59, 130, 246, 0.2)',
        zIndex: 1,
        textAlign: 'center',
        fontWeight: "700",
        fontSize: 13,
        color: '#334155',
        letterSpacing: 0.5,
        marginHorizontal: 16,
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(4px)',
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
        })
    },

    loginSocialScrollView: {
        borderRadius: 16,
        flex: 1,
        marginVertical: 8,
    },

    loginSocialButtonsContainer: {
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        padding: 16,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: 'rgba(203, 213, 225, 0.4)',
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(8px)',
            boxShadow: 'inset 0 2px 8px rgba(148, 163, 184, 0.08)',
        })
    },

    loginEmailSection: {
        backgroundColor: 'rgba(59, 130, 246, 0.03)',
        borderRadius: 20,
        borderWidth: 1,
        borderColor: 'rgba(59, 130, 246, 0.15)',
        flex: 1,
        minHeight: 180,
        paddingTop: 16,
        paddingHorizontal: 20,
        paddingVertical: 16,
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(12px)',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.08)',
        })
    },

    loginEmailDividerContainer: {
        alignSelf: "stretch",
        alignItems: "center",
        marginBottom: 16,
        maxHeight: 25,
    },

    loginEmailDividerText: {
        paddingHorizontal: 20,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 16,
        paddingVertical: 6,
        borderWidth: 1,
        borderColor: 'rgba(59, 130, 246, 0.25)',
        zIndex: 1,
        textAlign: 'center',
        fontWeight: "700",
        top: -12,
        fontSize: 12,
        color: '#3B82F6',
        letterSpacing: 0.5,
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(6px)',
            boxShadow: '0 2px 12px rgba(59, 130, 246, 0.15)',
        })
    },

    loginEmailDividerLine: {
        flex: 1,
        height: 2,
        backgroundColor: 'rgba(59, 130, 246, 0.3)',
        borderRadius: 1,
        marginHorizontal: 16,
    },

    loginInputContainer: {
        minHeight: 48,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: 'rgba(59, 130, 246, 0.3)',
        borderWidth: 2,
        borderRadius: 16,
        flexDirection: "row",
        alignSelf: "stretch",
        alignItems: "center",
        paddingHorizontal: 16,
        marginVertical: 6,
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.1), inset 0 2px 4px rgba(255, 255, 255, 0.5)',
            transition: 'all 0.2s ease',
        })
    },

    loginInput: {
        flex: 1,
        alignSelf: "stretch",
        fontSize: 15,
        height: 44,
        backgroundColor: 'transparent',
        borderWidth: 0,
        color: '#1e293b',
        fontWeight: '500',
        letterSpacing: 0.3,
    },

    loginPasswordToggle: {
        position: 'absolute',
        top: 5,
        paddingHorizontal: 5,
        right: 16,
        padding: 8,
        borderRadius: 8,
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(4px)',
        })
    },

    loginSubmitButton: {
        width: 220,
        marginVertical: 12,
        alignSelf: 'center',
        height: 48,
        backgroundColor: '#3B82F6',
        borderRadius: 16,
        borderWidth: 0,
        
        shadowColor: '#3B82F6',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(8px)',
            boxShadow: '0 6px 24px rgba(59, 130, 246, 0.4), 0 2px 8px rgba(59, 130, 246, 0.2)',
            transition: 'all 0.2s ease',
            ':hover': {
                backgroundColor: '#2563eb',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 4px 16px rgba(59, 130, 246, 0.3)',
            },
            ':active': {
                transform: 'translateY(0px)',
                boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)',
            }
        })
    },

    loginSubmitButtonText: {
        color: 'white',
        fontSize: 16,
        fontWeight: '700',
        letterSpacing: 0.5,
    },

    loginFooterLinks: {
        backgroundColor: 'rgba(248, 250, 252, 0.8)',
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderWidth: 1,
        borderColor: 'rgba(203, 213, 225, 0.3)',
        alignItems: 'center',
        flexDirection: 'row',
        gap: 16,
        marginTop: 8,
        minHeight: 40,
        maxHeight: 40,
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(4px)',
            boxShadow: 'inset 0 2px 4px rgba(148, 163, 184, 0.08)',
        })
    },

    loginFooterDivider: {
        width: 1,
        height: 20,
        backgroundColor: 'rgba(203, 213, 225, 0.6)',
        borderRadius: 0.5,
    },

    loginFooterText: {
        textAlign: "left",
        fontSize: 14,
        color: '#475569',
        flex: 2,
        justifyContent: 'center',
    },

    loginFooterLink: {
        color: "#3B82F6",
        fontWeight: '600',
        fontSize: 14,
        letterSpacing: 0.3,
        flex: 1,
        textAlign: "center",
    },

    // CompanySelector Modern Styles
    companySelectorContainer: {
        flexDirection: 'row',
        minWidth: 150,
        minHeight: 40,
        justifyContent: 'center',
        padding: 10,
        alignContent: 'center',
        maxHeight: 50,
    },

    companySelectorButton: {
        flex: 1,
        top: 3,
        flexDirection: "row",
        borderRadius: 16,
        
        shadowRadius: 12,
        shadowOpacity: 0.15,
        padding: 12,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderWidth: 1,
        borderColor: 'rgba(59, 130, 246, 0.1)',
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 20px rgba(59, 130, 246, 0.1)',
            transition: 'all 0.2s ease',
            ':hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 25px rgba(59, 130, 246, 0.15)',
            }
        })
    },

    companySelectorButtonDark: {
        backgroundColor: 'rgba(40, 40, 40, 0.95)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            ':hover': {
                backgroundColor: 'rgba(50, 50, 50, 1)',
                boxShadow: '0 6px 25px rgba(0, 0, 0, 0.4)',
            }
        })
    },

    companySelectorLogo: {
        justifyContent: "center",
        alignSelf: "center",
        maxHeight: '100%',
        left: -20,
        top: -3,
        minHeight: 50,
        overflow: 'hidden',
        resizeMode: "contain",
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(226, 232, 240, 0.5)',
    },

    companySelectorText: {
        fontWeight: '600',
        fontSize: 16,
        letterSpacing: 0.3,
    },

    companySelectorFallback: {
        minWidth: 100,
        flex: 1.5,
        
        overflow: "hidden",
        borderRadius: 16,
        backgroundColor: 'rgba(90, 192, 234, 0.1)',
        borderWidth: 1,
        borderColor: 'rgba(90, 192, 234, 0.2)',
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 16px rgba(90, 192, 234, 0.1)',
            transition: 'all 0.2s ease',
        })
    },

    companySelectorFallbackContent: {
        width: "100%",
        minHeight: 50,
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center',
        alignContent: 'center',
        alignItems: 'center',
        backgroundColor: 'transparent',
    },

    companySelectorFallbackHovered: {
        backgroundColor: 'rgb(90,192,234)',
        ...(Platform.OS === 'web' && {
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 20px rgba(90, 192, 234, 0.3)',
        })
    },

    companyDropdownOverlay: {
        top: -50,
        zIndex: -1,
        left: -80,
        alignContent: 'flex-end',
        minWidth: 360,
        alignItems: 'flex-end',
    },

    companyDropdownContainer: {
        flex: 1,
        padding: 20,
        paddingLeft: 30,
        paddingBottom: 150,
        position: 'absolute',
        minHeight: "100%",
        width: "35%",
        shadowOffset: { height: 15, width: 0 },
        maxWidth: 400,
        left: 90,
        top: 50,
        shadowOpacity: 0.2,
        flexDirection: 'column',
        flexWrap: 'nowrap',
        borderRadius: 24,
        borderStartStartRadius: 0,
        borderStartEndRadius: 0,
        gap: 16,
        shadowRadius: 25,
        zIndex: -10,
        
        borderWidth: 1,
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        })
    },

    companyDropdownContainerLight: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: 'rgba(0, 0, 0, 0.08)',
    },

    companyDropdownContainerDark: {
        backgroundColor: 'rgba(40, 40, 40, 0.95)',
        borderColor: 'rgba(255, 255, 255, 0.12)',
    },

    companyDropdownHeader: {
        flexDirection: 'row',
        alignSelf: "stretch",
        alignItems: 'center',
        marginBottom: 8,
    },

    companyDropdownTitle: {
        userSelect: "none",
        flex: 5,
        textAlign: 'left',
        justifyContent: "flex-start",
        alignSelf: 'stretch',
        fontWeight: '700',
        fontSize: 18,
        letterSpacing: 0.5,
    },

    companyDropdownHelpButton: {
        flex: 1,
        alignSelf: 'flex-end',
        marginVertical: 4,
        padding: 8,
        borderRadius: 12,
        backgroundColor: 'rgba(90, 192, 234, 0.1)',
        alignItems: 'center',
        justifyContent: 'center',
        ...(Platform.OS === 'web' && {
            transition: 'all 0.2s ease',
            ':hover': {
                backgroundColor: 'rgba(90, 192, 234, 0.2)',
                transform: 'scale(1.05)',
            }
        })
    },

    companySectionList: {
        alignSelf: 'stretch',
        minWidth: '80%',
        borderRadius: 16,
        
        shadowRadius: 10,
        shadowOpacity: 0.1,
        shadowOffset: { width: 0, height: 4 },
        borderWidth: 1,
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        })
    },

    companySectionListLight: {
        backgroundColor: 'rgba(248, 250, 252, 0.9)',
        borderColor: 'rgba(0, 0, 0, 0.05)',
    },

    companySectionListDark: {
        backgroundColor: 'rgba(20, 20, 20, 0.9)',
        borderColor: 'rgba(255, 255, 255, 0.08)',
    },

    companySectionHeader: {
        paddingHorizontal: 20,
        paddingVertical: 12,
        fontWeight: '700',
        fontSize: 16,
        marginBottom: 8,
        letterSpacing: 0.5,
        backgroundColor: 'rgba(90, 192, 234, 0.1)',
        borderRadius: 12,
        marginHorizontal: 8,
    },

    companyListItem: {
        marginVertical: 4,
        borderRadius: 16,
        overflow: 'hidden',
    },

    companyListItemCurrent: {
        backgroundColor: 'rgba(90,192,234,0.1)',
        borderWidth: 2,
        borderColor: 'rgb(90,192,234)',
    },

    companyListItemLight: {
        backgroundColor: 'rgba(255,255,255,0.8)',
    },

    companyListItemDark: {
        backgroundColor: 'rgba(255,255,255,0.05)',
    },

    companyListItemContent: {
        flex: 1,
        borderRadius: 14,
        padding: 16,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        minHeight: 72,
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        
    },

    companyListItemHovered: {
        backgroundColor: "rgba(90,192,234, 0.1)",
    },

    companyLogo: {
        resizeMode: "contain",
        borderRadius: 12,
        height: 48,
        width: 48,
        borderWidth: 1,
        borderColor: 'rgba(226, 232, 240, 0.5)',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
    },

    companyInfo: {
        flex: 1,
        gap: 4,
    },

    companyName: {
        fontWeight: '600',
        fontSize: 16,
        letterSpacing: 0.3,
    },

    companyTagline: {
        fontSize: 14,
        opacity: 0.8,
        letterSpacing: 0.2,
    },

    demoLabel: {
        position: "absolute",
        right: 12,
        top: 12,
        backgroundColor: "#6b7280",
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 8,
        shadowRadius: 8,
        shadowOpacity: 0.2,
        
    },

    demoLabelText: {
        color: "white",
        fontSize: 12,
        fontWeight: "600",
        letterSpacing: 0.5,
    },

    addCompanyButton: {
        backgroundColor: '#3B82F6',
        borderRadius: 16,
        
        shadowColor: '#3B82F6',
        shadowOpacity: 0.3,
        shadowRadius: 12,
        shadowOffset: { width: 0, height: 4 },
        borderWidth: 0,
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(8px)',
            boxShadow: '0 6px 24px rgba(59, 130, 246, 0.4)',
            transition: 'all 0.2s ease',
            ':hover': {
                backgroundColor: '#2563eb',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5)',
            },
            ':active': {
                transform: 'translateY(0px)',
            }
        })
    },

    // Coming Soon Component Styles
    comingSoonContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
        minHeight: 400,
    },

    comingSoonContent: {
        borderRadius: 24,
        padding: 32,
        alignItems: 'center',
        maxWidth: 500,
        width: '100%',
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 12,
        },
        shadowOpacity: 0.15,
        shadowRadius: 24,
        
        borderWidth: 1,
        borderColor: 'rgba(90,192,234,0.2)',
        ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(20px)',
        })
    },

    comingSoonAnimationContainer: {
        marginBottom: 24,
        alignItems: 'center',
        justifyContent: 'center',
    },

    comingSoonAnimation: {
        width: 120,
        height: 120,
    },

    comingSoonPlaceholder: {
        width: 120,
        height: 120,
        borderRadius: 60,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderColor: 'rgba(90,192,234,0.3)',
    },

    comingSoonPlaceholderIcon: {
        fontSize: 48,
        textAlign: 'center',
    },

    comingSoonTextContainer: {
        alignItems: 'center',
        marginBottom: 32,
        width: '100%',
    },

    comingSoonTitle: {
        fontSize: 32,
        fontWeight: '700',
        textAlign: 'center',
        marginBottom: 8,
        letterSpacing: 0.5,
    },

    comingSoonSubtitle: {
        fontSize: 18,
        fontWeight: '500',
        textAlign: 'center',
        marginBottom: 24,
        letterSpacing: 0.3,
    },

    comingSoonDivider: {
        width: 60,
        height: 3,
        borderRadius: 2,
        marginBottom: 24,
    },

    comingSoonMessage: {
        fontSize: 16,
        lineHeight: 24,
        textAlign: 'center',
        letterSpacing: 0.2,
        maxWidth: 400,
    },

    comingSoonProgressContainer: {
        width: '100%',
        alignItems: 'center',
    },

    comingSoonProgressBar: {
        width: '100%',
        height: 6,
        borderRadius: 3,
        marginBottom: 12,
        overflow: 'hidden',
    },

    comingSoonProgressFill: {
        height: '100%',
        borderRadius: 3,
    },

    comingSoonProgressText: {
        fontSize: 14,
        fontWeight: '500',
        letterSpacing: 0.5,
    },

    // Coming Soon Modal Styles
    comingSoonModalOverlay: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },

    comingSoonModalBackdrop: {
        flex: 1,
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
    },

    comingSoonModalWrapper: {
        maxWidth: 500,
        width: '100%',
        maxHeight: '90%',
        position: 'relative',
    },

    comingSoonModalContent: {
        borderRadius: 24,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 20,
        },
        shadowOpacity: 0.25,
        shadowRadius: 30,
        
    },

    comingSoonModalCloseButton: {
        position: 'absolute',
        width: 40,
        height: 40,
        borderRadius: 20,
        zIndex: 10,
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 4,
        },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        
    },

    comingSoonModalCloseButtonInner: {
        flex: 1,
        top: 20,
        left: 20,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 20,
    },

    comingSoonModalInnerContainer: {
        padding: 32,
        minHeight: 400,
    },

    // Modern Calendar Styles
    calendarContainer: {
        borderRadius: 16,
        backgroundColor: 'transparent',
        paddingHorizontal: 16,
        paddingVertical: 20,
    },

    calendarHeader: {
        backgroundColor: 'transparent',
        borderBottomWidth: 0,
        paddingBottom: 16,
        marginBottom: 16,
    },

    calendarHeaderTitle: {
        fontSize: 24,
        fontWeight: '700',
        color: '#1e293b',
        fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
    },

    calendarHeaderTitleDark: {
        color: '#f8fafc',
    },

    calendarMonth: {
        backgroundColor: 'transparent',
        paddingHorizontal: 0,
    },

    calendarDayCell: {
        borderRadius: 8,
        margin: 2,
        backgroundColor: 'transparent',
        minHeight: 48,
        alignItems: 'center',
        justifyContent: 'center',
        ...(Platform.OS === 'web' && {
            transition: 'all 0.2s ease',
        }),
    },

    calendarDayToday: {
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderColor: '#3b82f6',
        borderWidth: 2,
        borderRadius: 8,
    },

    calendarDaySelected: {
        backgroundColor: '#3b82f6',
        borderRadius: 8,
        shadowColor: '#3b82f6',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
        
    },

    calendarDayText: {
        fontSize: 14,
        fontWeight: '500',
        color: '#1e293b',
        fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
    },

    calendarDayTextDark: {
        color: '#f8fafc',
    },

    calendarDayTextSelected: {
        color: 'white',
        fontWeight: '600',
    },

    calendarDayTextToday: {
        color: '#3b82f6',
        fontWeight: '600',
    },

    calendarDayTextDisabled: {
        color: '#94a3b8',
    },

    calendarEventCard: {
        backgroundColor: '#3b82f6',
        borderRadius: 8,
        marginHorizontal: 2,
        marginVertical: 1,
        paddingHorizontal: 8,
        paddingVertical: 4,
        minHeight: 28,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 6,
        
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.15)',
        alignItems: 'center',
        justifyContent: 'center',
    },

    calendarEventText: {
        color: 'white',
        fontSize: 10,
        fontWeight: '600',
        textAlign: 'center',
        fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
    },

    calendarEventCount: {
        fontSize: 12,
        fontWeight: '600',
        color: '#64748b',
        fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
    },

    calendarEventCountDark: {
        color: '#94a3b8',
    },

    calendarArrow: {
        padding: 8,
        borderRadius: 8,
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        alignItems: 'center',
        justifyContent: 'center',
        margin: 4,
        ...(Platform.OS === 'web' && {
            transition: 'all 0.2s ease',
        }),
    },

    calendarArrowDark: {
        backgroundColor: 'rgba(147, 197, 253, 0.2)',
    },
});

// Export utility functions for modern design system
export { 
  getModernCardStyle, 
  getModernActionStyle, 
  getModernTextColor,
    // Unified responsive functions (work on both web and native)
    getResponsiveValue,
    responsivePadding,
    responsiveFontSize,
    responsiveHeight,
    // Legacy web-only functions (kept for backward compatibility)
  getWebResponsiveValue,
  webResponsivePadding,
  webResponsiveFontSize,
  webResponsiveHeight
};

export default styles;